import type {
  CastVoteResponse,
  FeedbackVote,
  MyVoteResponse,
  ReasonCode,
  ReceivedFeedbackResponse,
  TalliesResponse,
  VoteDirection,
} from "@skills-marketplace/contracts/contract-8-feedback";
import { authHeaders, baseUrl, handleResponse, proxyFetch } from "./github-proxy-client";
import { env } from "./env";
import { reportEvent } from "./newrelic";

/**
 * Client for the community feedback API (Contract 8, issues #617/#618).
 *
 * Transport is shared with github-proxy-client — same API Gateway, same Entra
 * audience — so there is exactly one copy of the token/base-URL/dev-short-circuit
 * logic rather than two that can drift.
 *
 * The read and write paths have deliberately different failure postures, and that
 * asymmetry is the important thing about this module:
 *
 *   READS never throw. A tally is decoration on a catalog that must render without
 *   it, so every failure resolves to null and the UI shows its one empty state. Same
 *   discipline as fetchAdoption.
 *
 *   WRITES do throw. A vote that silently failed would be worse than an error: the
 *   voter believes they were counted and they were not. The caller surfaces it.
 */

/**
 * Where to read tallies from.
 *
 * REACT_APP_FEEDBACK_URL overrides the endpoint with a static file, which is how
 * local dev sees tallies without a deployed backend (`/mock-feedback.json`). It is a
 * read-path override only — writes always go to the API.
 */
export function talliesUrl(): string {
  return env.REACT_APP_FEEDBACK_URL || `${baseUrl()}/feedback/tallies`;
}

/**
 * Every entry's vote tallies, or null when unavailable.
 *
 * NEVER throws and never rejects. A missing endpoint, a 401, a non-2xx, malformed
 * JSON, an unexpected schema version and a token failure all resolve to null,
 * because none of them should stop the catalog rendering — an absent tally is
 * indistinguishable from "nobody has voted", which is a state the UI already draws.
 *
 * In dev with no fixture configured this is expected to fail (the dev server has no
 * /feedback/tallies to serve), so it warns rather than reporting an error.
 */
export async function fetchFeedbackTallies(): Promise<TalliesResponse | null> {
  const url = talliesUrl();

  try {
    const headers = await authHeaders();
    const response = await proxyFetch(url, { method: "GET", headers });

    if (!response.ok) {
      console.warn(`[feedback] tallies unavailable (${response.status}) — showing empty state`);
      return null;
    }

    const body = (await response.json()) as TalliesResponse;

    // Guard the shape rather than trusting it: this is parsed JSON, and a wrong
    // major version means the map may not be what we think it is.
    if (!body || typeof body !== "object" || !body.entries || typeof body.entries !== "object") {
      console.warn("[feedback] tallies response was not the expected shape — showing empty state");
      return null;
    }
    if (typeof body.schemaVersion === "string" && !body.schemaVersion.startsWith("1.")) {
      console.warn(
        `[feedback] tallies schemaVersion ${body.schemaVersion} is not compatible — showing empty state`,
      );
      return null;
    }

    return body;
  } catch (err) {
    console.warn("[feedback] tallies could not be fetched — showing empty state", err);
    return null;
  }
}

/**
 * The caller's own vote on one entry, or null if they have not voted.
 *
 * Also non-throwing: not knowing your prior vote degrades to showing the prompt
 * again, which is recoverable, whereas failing the page is not.
 */
export async function fetchMyVote(installName: string): Promise<FeedbackVote | null> {
  try {
    const headers = await authHeaders();
    const params = new URLSearchParams({ installName });
    const response = await proxyFetch(`${baseUrl()}/feedback/mine?${params.toString()}`, {
      method: "GET",
      headers,
    });
    if (!response.ok) return null;

    const body = (await response.json()) as MyVoteResponse;
    return body?.vote ?? null;
  } catch {
    return null;
  }
}

/**
 * The feedback left on ONE of the caller's own entries — the plugin plus its skills
 * (#621). Returns null when unavailable.
 *
 * A READ, so it never throws: a 404 (the caller does not own this entry, or it has no
 * feedback), a non-2xx, a rejected fetch, malformed JSON, or an incompatible schema
 * version all resolve to null. The dashboard treats null as "couldn't load — offer a
 * retry", distinct from an empty `groups` array ("no feedback yet"). Same read-path
 * discipline as fetchFeedbackTallies.
 *
 * Every one of those failure modes WARNS before returning null, carrying the status
 * code where there is one. Returning a bare null used to throw the diagnostic detail
 * away: the caller reports "it failed" to New Relic (MySkillsPage's reportError) but
 * only the console can say WHICH failure it was. Same warn-per-branch pattern as
 * fetchFeedbackTallies above.
 */
export async function getReceivedFeedback(
  installName: string,
): Promise<ReceivedFeedbackResponse | null> {
  try {
    const headers = await authHeaders();
    const params = new URLSearchParams({ installName });
    const response = await proxyFetch(`${baseUrl()}/feedback/received?${params.toString()}`, {
      method: "GET",
      headers,
    });
    if (!response.ok) {
      console.warn(
        `[feedback] received feedback unavailable for ${installName} (${response.status}) — offering retry`,
      );
      return null;
    }

    const body = (await response.json()) as ReceivedFeedbackResponse;
    if (!body || typeof body !== "object" || !Array.isArray(body.groups)) {
      console.warn(
        `[feedback] received-feedback response for ${installName} was not the expected shape — offering retry`,
      );
      return null;
    }
    if (typeof body.schemaVersion === "string" && !body.schemaVersion.startsWith("1.")) {
      console.warn(
        `[feedback] received-feedback schemaVersion ${body.schemaVersion} is not compatible — offering retry`,
      );
      return null;
    }
    return body;
  } catch (err) {
    console.warn(`[feedback] received feedback for ${installName} could not be fetched`, err);
    return null;
  }
}

/**
 * Cast, change, or re-submit the caller's vote, returning the entry's new tally.
 *
 * ONE call carrying the direction and its reasons together — that is #617's "the
 * vote and its reason commit together" on the wire. No counts are sent: the server
 * computes the delta from the voter's prior state.
 *
 * Throws on failure, deliberately. See the module note.
 */
export async function castVote(
  installName: string,
  direction: VoteDirection,
  reasonCodes: ReasonCode[],
  reasonText: string | null,
  reasonDetails?: Partial<Record<ReasonCode, string>>,
): Promise<CastVoteResponse> {
  const headers = await authHeaders();
  const response = await proxyFetch(`${baseUrl()}/feedback`, {
    method: "POST",
    headers,
    body: JSON.stringify({ installName, direction, reasonCodes, reasonText, reasonDetails }),
  });
  return handleResponse<CastVoteResponse>(response);
}

/** Withdraw the caller's vote and its reasons together (Undo). Throws on failure. */
export async function withdrawVote(installName: string): Promise<CastVoteResponse> {
  const headers = await authHeaders();
  const response = await proxyFetch(`${baseUrl()}/feedback`, {
    method: "DELETE",
    headers,
    body: JSON.stringify({ installName }),
  });
  return handleResponse<CastVoteResponse>(response);
}

/**
 * Report a committed vote for the participation metric (#617's primary).
 *
 * Fired only after the write succeeds — instrumenting the click instead would count
 * votes that never landed and inflate the numerator. Epic #506 requires these events
 * at build time rather than as a post-launch addition.
 *
 * Deliberately carries no voter identity and no free text: the entry and the shape
 * of the response are enough to compute participation, and a private reason must not
 * become an analytics attribute.
 */
export function reportVoteEvent(
  installName: string,
  direction: VoteDirection,
  reasonCodeCount: number,
  hasFreeText: boolean,
): void {
  // No try/catch needed: reportEvent no-ops when the agent is absent and swallows
  // its own failures, so instrumentation cannot break the interaction it measures.
  reportEvent("marketplace_vote_cast", {
    installName,
    direction,
    reasonCodeCount,
    hasFreeText,
  });
}
